import React, { useState, useEffect } from 'react';
import { MessageCircle, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WHATSAPP_URL = 'https://wa.me/601133131844?text=Hi%20TAF%20Worldwide!%20I%20am%20interested%20in%20your%20cinematography%20services.';

// Track Contact event for Meta Pixel
const trackWhatsAppClick = (source) => {
  if (typeof window.fbq === 'function') {
    const eventId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    window.fbq('track', 'Contact', { 
      content_name: 'WhatsApp Chat',
      content_category: source,
      eventID: eventId
    });
    console.log(`[Meta Pixel] Contact tracked from ${source}`, { eventId });
  }
};

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

/**
 * Sticky WhatsApp bar — mobile only, hides near Contact section
 */
export const StickyWhatsApp = () => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const contact = document.getElementById('contact');
    if (!contact) return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    io.observe(contact);
    return () => io.disconnect();
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="bg-black/90 border-t border-white/10 px-4 py-3">
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sticky-whatsapp-btn"
          className="flex items-center justify-center gap-2.5 w-full py-3 rounded-xl text-white font-bold text-sm transition-all active:scale-95"
          style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
        >
          <WhatsAppIcon />
          Chat WhatsApp. Book Your Shoot
        </a>
      </div>
    </div>
  );
};

/**
 * CTA Pop-up — appears after 50% scroll, urgency message
 */
export const CTAPopup = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const currentMonth = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });

  useEffect(() => {
    if (sessionStorage.getItem('taf_cta_dismissed')) {
      setDismissed(true);
      return;
    }

    const onScroll = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrollPercent > 0.5 && !dismissed) {
        setShow(true);
        window.removeEventListener('scroll', onScroll);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    sessionStorage.setItem('taf_cta_dismissed', '1');
  };

  if (!show || dismissed) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleDismiss} />
      <div
        className="relative w-full sm:max-w-md mx-auto bg-gray-950 border border-orange-500/30 rounded-t-2xl sm:rounded-2xl p-6 sm:p-8 animate-[slideUp_0.4s_ease]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 w-8 h-8 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
          data-testid="cta-popup-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-500/15 border border-red-500/30 rounded-full mb-4">
            <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
            <span className="text-red-400 text-[10px] font-bold uppercase tracking-wider">Limited Slots</span>
          </div>

          <h3 className="text-xl sm:text-2xl font-black text-white mb-2" style={{ fontFamily: "'TafHeadline'", letterSpacing: '0.03em' }}>
            Slots for {currentMonth} are filling up fast
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            Book your shoot now before we're fully booked.
          </p>

          <div className="space-y-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('CTA Popup')}
              data-testid="cta-popup-whatsapp"
              className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl text-white font-bold text-sm transition-all hover:scale-[1.02] active:scale-95"
              style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
            >
              <WhatsAppIcon />
              Chat Us on WhatsApp
            </a>
            <button
              onClick={() => { handleDismiss(); navigate('/shoot-with-us'); }}
              data-testid="cta-popup-form"
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-white/5 border border-white/10 hover:border-orange-500/40 rounded-xl text-white font-semibold text-sm transition-all hover:bg-white/10"
            >
              Submit Inquiry Form
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
